<template>
  <StaticPageLayout
    title="Gizlilik Politikası - INSATEQ"
    description="INSATEQ gizlilik politikası ve veri koruma uygulamaları. Kişisel bilgilerinizin nasıl toplandığı, kullanıldığı ve korunduğu hakkında detaylı bilgi."
  >
    <!-- Hero -->
    <div class="bg-gradient-to-br from-blue-600 to-indigo-700 py-16">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 class="text-3xl md:text-4xl font-bold text-white mb-4">
          Gizlilik Politikası
        </h1>
        <p class="text-blue-100 text-lg">
          Son güncelleme: {{ new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' }) }}
        </p>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div class="prose prose-lg max-w-none">
        <!-- Section 1 -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">1</span>
            Giriş
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            INSATEQ olarak, kişisel verilerinizin güvenliği ve gizliliği bizim için son derece önemlidir.
            Bu Gizlilik Politikası, platformumuz aracılığıyla toplanan kişisel verilerin nasıl işlendiği,
            kullanıldığı, saklandığı ve korunduğunu açıklamaktadır.
          </p>
          <p class="text-gray-600 leading-relaxed">
            6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") ve ilgili mevzuat kapsamında,
            veri sorumlusu sıfatıyla hareket etmekteyiz.
          </p>
        </section>

        <!-- Section 2 -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">2</span>
            Toplanan Veriler
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            Hizmetlerimizi kullanırken aşağıdaki kişisel verilerinizi toplayabiliriz:
          </p>
          <div class="bg-gray-50 rounded-xl p-6 space-y-3">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <p class="text-gray-700"><strong>Kimlik Bilgileri:</strong> Ad, soyad, TC kimlik numarası</p>
            </div>
            <div class="flex items-start">
              <svg class="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <p class="text-gray-700"><strong>İletişim Bilgileri:</strong> E-posta adresi, telefon numarası, adres</p>
            </div>
            <div class="flex items-start">
              <svg class="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <p class="text-gray-700"><strong>Hesap Bilgileri:</strong> Kullanıcı adı, şifre (şifrelenmiş)</p>
            </div>
            <div class="flex items-start">
              <svg class="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <p class="text-gray-700"><strong>İş İlişkisi Bilgileri:</strong> Şirket adı, pozisyon, departman</p>
            </div>
            <div class="flex items-start">
              <svg class="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <p class="text-gray-700"><strong>Teknik Veriler:</strong> IP adresi, tarayıcı bilgileri, çerezler</p>
            </div>
          </div>
        </section>

        <!-- Section 3 -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">3</span>
            Verilerin Kullanım Amaçları
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            Toplanan kişisel verileriniz aşağıdaki amaçlarla işlenmektedir:
          </p>
          <ul class="list-disc pl-6 space-y-2 text-gray-600">
            <li>Hesap oluşturma ve kimlik doğrulama işlemleri</li>
            <li>İnşaat proje yönetimi hizmetlerinin sunulması</li>
            <li>Personel ve zaman yönetimi işlemleri</li>
            <li>Fatura ve ödeme işlemlerinin gerçekleştirilmesi</li>
            <li>Müşteri destek hizmetlerinin sağlanması</li>
            <li>Platform güvenliğinin ve bütünlüğünün korunması</li>
            <li>Yasal yükümlülüklerin yerine getirilmesi</li>
          </ul>
        </section>

        <!-- Section 4 -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">4</span>
            Verilerin Saklanması ve Güvenliği
          </h2>
          <div class="bg-green-50 border border-green-200 rounded-xl p-6 mb-4">
            <div class="flex items-start">
              <svg class="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <div>
                <p class="font-medium text-green-800">Verileriniz güvenli ellerde</p>
                <p class="text-green-700 text-sm mt-1">SSL sertifikası, güvenlik duvarları ve veri şifreleme ile korunmaktadır.</p>
              </div>
            </div>
          </div>
          <ul class="list-disc pl-6 space-y-2 text-gray-600">
            <li>Güvenli sunucularda, şifrelenmiş olarak saklanır</li>
            <li>Yalnızca yetkili personel tarafından erişilebilir</li>
            <li>İşleme amacının gerektirdiği süre boyunca muhafaza edilir</li>
            <li>Yasal saklama süreleri sonunda güvenli bir şekilde silinir</li>
          </ul>
        </section>

        <!-- Section 5 -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">5</span>
            Haklarınız
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            KVKK kapsamında, kişisel verilerinizle ilgili aşağıdaki haklara sahipsiniz:
          </p>
          <div class="grid md:grid-cols-2 gap-4">
            <div class="bg-gray-50 rounded-lg p-4">
              <p class="text-gray-700 text-sm">Kişisel verilerinizin işlenip işlenmediğini öğrenme</p>
            </div>
            <div class="bg-gray-50 rounded-lg p-4">
              <p class="text-gray-700 text-sm">İşlenmişse buna ilişkin bilgi talep etme</p>
            </div>
            <div class="bg-gray-50 rounded-lg p-4">
              <p class="text-gray-700 text-sm">Eksik veya yanlış işlenmişse düzeltilmesini isteme</p>
            </div>
            <div class="bg-gray-50 rounded-lg p-4">
              <p class="text-gray-700 text-sm">KVKK'da öngörülen şartlarda silinmesini isteme</p>
            </div>
          </div>
        </section>

        <!-- Section 6 - Contact -->
        <section class="mb-10">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
            <span class="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold mr-3">6</span>
            İletişim
          </h2>
          <p class="text-gray-600 leading-relaxed mb-4">
            Kişisel verilerinizle ilgili taleplerinizi aşağıdaki yöntemlerle iletebilirsiniz:
          </p>
          <div class="bg-blue-50 rounded-xl p-6">
            <div class="flex items-center mb-3">
              <svg class="w-5 h-5 text-blue-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <a href="mailto:kvkk@insateq.com" class="text-blue-600 hover:text-blue-700 font-medium">kvkk@insateq.com</a>
            </div>
            <p class="text-gray-600 text-sm">
              Başvurularınız, talebin niteliğine göre en geç 30 gün içinde ücretsiz olarak sonuçlandırılacaktır.
            </p>
          </div>
        </section>
      </div>
    </div>
  </StaticPageLayout>
</template>

<script setup>
import StaticPageLayout from '@/Layouts/StaticPageLayout.vue'
</script>
